import React, { useMemo } from 'react';

import { DndContext, DragOverlay } from '@dnd-kit/core';
import cn from 'classnames';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { showToast } from '@features';
import { getNameExtension } from '@shared/helpers';
import { useNuqsQueryState } from '@shared/hooks';
import { FileDropZone, Flex, Icon, UiButton, UiText } from '@shared/ui';
import {
    useGetOrdersRecognitionList,
    useSearchOrders,
    useUploadOrder,
} from '@widgets/OrdersRecognition/api/hooks';
import { useOrdersRecognitionContext } from '@widgets/OrdersRecognition/lib/hooks/useOrdersRecognitionContext';
import { ORDERS_HIDE_PROCEEDED, ORDERS_TOAST_SCOPE } from '@widgets/OrdersRecognition/model/сonsts';

import cls from './OrdersRecognitionMain.module.css';
import { Order, OrderDragPreview, FilePreload, PositionDragPreview } from '..';
import {
    useDragItems,
    useHandleComment,
    useUploadOrderItems,
    useHandleUploadOrder,
} from '../../lib/hooks';

interface IOrdersRecognitionMainProps {
    openFull: () => void;
    openHalf: () => void;
}

// TODO тут нужно определенно прибраться!

export const OrdersRecognitionMain = ({ openFull, openHalf }: IOrdersRecognitionMainProps) => {
    const { t } = useTranslation();
    const { activeDrag, handleDragMove, handleDragStart, handleDragEnd } = useDragItems({
        onEnterSidebar: openFull,
        onLeaveSidebar: openHalf,
    });
    // const { getAttachmentInfo } = useAttachmentsList({
    //     onDeleteAttachmentCb: () => console.log('delete!'),
    // });
    const { imgUrl, droppedFiles, fileReplaceLoader, onUploadHandler, clearDroppedFiles } =
        useUploadOrderItems();
    const { comment, onChangeComment, clearComment } = useHandleComment();
    const { mutateAsync: uploadOrder, isPending } = useUploadOrder({
        onSuccessCb: () => {
            clearDroppedFiles();
            clearComment();
            showToast(t('OrderRecognition.Toast.addOrder.success.text'), {
                scope: ORDERS_TOAST_SCOPE,
                type: 'success',
            });
        },
        onErrorCb: (errText) => {
            showToast(errText, { scope: ORDERS_TOAST_SCOPE, type: 'error' });
        },
    });
    const { uploadOrderHandler, isUploadingFile } = useHandleUploadOrder({
        dataToUpload: {
            file: droppedFiles[0]?.file,
            comment: comment?.trim(),
        },
        uploadOrderCb: uploadOrder,
    });
    const { data: ordersData, isFetching } = useGetOrdersRecognitionList();
    const { searchDraft: searchQueryValue } = useOrdersRecognitionContext();
    const { data: searchData, appliedQuery, isFetching: isSearchFetching } = useSearchOrders();
    const { state: hideProceedCheckbox } = useNuqsQueryState(ORDERS_HIDE_PROCEEDED);

    const ext = getNameExtension(droppedFiles[0]?.file?.name);

    const dataToRender = useMemo(() => {
        if (hideProceedCheckbox === 'true') {
            const dataToFilter =
                searchQueryValue && searchQueryValue.length > 0
                    ? searchData?.purchaseDocuments
                    : ordersData?.purchaseDocuments;

            return dataToFilter?.filter((order) => !order.isFullyUsed);
        }
        return ordersData?.purchaseDocuments;
    }, [ordersData, searchData, searchQueryValue, hideProceedCheckbox]);

    // const listToRender = useMemo(() => {
    //     if (!appliedQuery) return dataToRender;
    //
    //     // if (isSearchFetching) return dataToRender;
    //
    //     if (appliedQuery && appliedQuery.length > 0 && hideProceedCheckbox === 'true') {
    //         return dataToRender;
    //     }
    //
    //     return searchData?.purchaseDocuments ?? [];
    // }, [appliedQuery, dataToRender, searchData, hideProceedCheckbox]);

    const listToRender = useMemo(() => {
        if (!appliedQuery) return dataToRender;

        // if (!searchData?.purchaseDocuments) return dataToRender;

        if (hideProceedCheckbox === 'true') return dataToRender;

        return searchData?.purchaseDocuments;
    }, [appliedQuery, dataToRender, searchData, hideProceedCheckbox]);

    // useEffect(() => {
    //     const info = getAttachmentInfo(droppedFiles[0]);
    //     console.log('droppedFiles', droppedFiles);
    //     console.log('info', info);
    //     console.log('ext', ext);
    // }, [droppedFiles, getAttachmentInfo, ext]);

    return (
        <main className={cn(cls.sidebarMain, isFetching && cls.isFetching)}>
            <FileDropZone
                multiple={false}
                onFilesDrop={(dropped) => {
                    const [file] = dropped;
                    if (!file) return;
                    onUploadHandler(dropped);
                    clearComment();
                }}
                overlayText={t('OrderRecognition.Main.FileDropZone.OnDrop.text')}
                disabled={isUploadingFile || !ordersData?.isCanEdit}
            >
                {({ openFileDialog }) => (
                    <Flex direction="column" align="start" gap={25}>
                        {ordersData?.isCanEdit && (
                            <Flex align="center" gap={8} className={cls.fileDropZoneBtnWrapper}>
                                <UiButton
                                    startIcon={<Icon iconName="plus" />}
                                    btnText={t('OrderRecognition.Main.FileDropZone.btn.text')}
                                    onClick={openFileDialog}
                                    disabled={isUploadingFile}
                                />
                                <UiText variant="mediumPlain" className={cls.fileBtnSpan}>
                                    {t('OrderRecognition.Main.FileDropZone.btnSpan.text')}
                                </UiText>
                            </Flex>
                        )}
                        {droppedFiles && !!droppedFiles.length && (
                            <FilePreload
                                droppedFiles={droppedFiles}
                                fileId={droppedFiles[0]?.id}
                                fileName={droppedFiles[0].file?.name}
                                fileExtension={ext.extension}
                                imgUrl={imgUrl}
                                comment={comment}
                                isLoading={isPending}
                                onSubmitHandler={uploadOrderHandler}
                                onChangeComment={onChangeComment}
                                fileClassName={cn(fileReplaceLoader && cls.isFetching)}
                            />
                        )}
                        <DndContext
                            onDragStart={handleDragStart}
                            onDragMove={handleDragMove}
                            onDragEnd={handleDragEnd}
                        >
                            <Flex
                                direction="column"
                                className={cn(
                                    cls.ordersWrapper,
                                    (isFetching || isSearchFetching || isUploadingFile) &&
                                        cls.isFetching,
                                    !ordersData?.isCanEdit && cls.paddingTop_24
                                )}
                            >
                                {/* {searchQueryValue.length > 0 && */}
                                {/*    searchData?.map((order) => ( */}
                                {/*        <Order */}
                                {/*            key={order.id} */}
                                {/*            id={order.id} */}
                                {/*            name={order.displayString} */}
                                {/*            avatarSrc={order.createdBy?.avatar} */}
                                {/*            orderPositions={order?.positions} */}
                                {/*        /> */}
                                {/*    ))} */}
                                {/* {(!searchQueryValue || searchQueryValue.length === 0) && */}
                                {/*    dataToRender?.map((order) => ( */}
                                {/*        <Order */}
                                {/*            key={order.id} */}
                                {/*            id={order.id} */}
                                {/*            name={order.displayString} */}
                                {/*            avatarSrc={order.createdBy?.avatar} */}
                                {/*            orderPositions={order?.positions} */}
                                {/*        /> */}
                                {/*    ))} */}
                                {listToRender?.map((order) => (
                                    <Order
                                        key={order.id}
                                        orderData={order}
                                        isCanEdit={ordersData?.isCanEdit}
                                    />
                                ))}
                            </Flex>
                            {createPortal(
                                <div className={cls.overlayContainer}>
                                    <DragOverlay
                                        style={{ pointerEvents: 'none' }}
                                        dropAnimation={null}
                                    >
                                        {activeDrag &&
                                            (activeDrag.type === 'order' ? (
                                                <OrderDragPreview name={activeDrag.item.name} />
                                            ) : (
                                                <PositionDragPreview name={activeDrag.item.name} />
                                            ))}
                                    </DragOverlay>
                                </div>,
                                document.body
                            )}
                        </DndContext>
                    </Flex>
                )}
            </FileDropZone>
        </main>
    );
};
